/**
 * Health API Service
 * 
 * Feature: 036-unified-dashboard
 * Description: Frontend API client for health monitoring operations
 * Constitution: v1.11.0
 */

import { apiClient } from './apiClient';
import { AxiosResponse } from 'axios';

const API_BASE = '/api/health';

export interface HealthOverview {
  overallScore: number;
  status: 'Healthy' | 'Degraded' | 'Critical' | 'Unknown';
  components: ComponentHealth[];
  lastUpdated: string;
  capacityTypeCounts: Record<string, number>;
}

export interface ComponentHealth {
  name: string;
  status: string;
  healthScore: number;
  lastChecked: string;
  metrics: Record<string, unknown>;
}

export interface HealthTrend {
  timestamp: string;
  score: number;
  status: string;
}

export interface Alert {
  id: string;
  severity: 'critical' | 'high' | 'medium' | 'low' | 'info';
  title: string;
  message: string;
  source: string;
  capacityType?: string;
  timestamp: string;
  acknowledged: boolean;
  resolvedAt?: string;
}

export interface AlertSummary {
  critical: number;
  high: number;
  medium: number;
  low: number;
  info: number;
  total: number;
}

export interface DependencyHealth {
  id: string;
  name: string;
  type: string;
  status: string;
  healthScore: number;
  impactLevel: 'critical' | 'high' | 'medium' | 'low';
  dependencies: string[];
  dependents: string[];
}

export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
}

/**
 * Handle axios response and convert to ApiResponse format
 * Note: Backend already returns { success, data } format, so we unwrap it
 */
async function handleAxiosResponse<T>(promise: Promise<AxiosResponse<T | ApiResponse<T>>>): Promise<ApiResponse<T>> {
  try {
    const response = await promise;
    const responseData = response.data as T | ApiResponse<T>;
    
    // Check if the backend already wrapped the response in { success, data }
    if (responseData && typeof responseData === 'object' && 'success' in responseData && 'data' in responseData) {
      // Backend already wrapped it - unwrap and return
      const wrapped = responseData as ApiResponse<T>;
      return {
        success: wrapped.success,
        data: wrapped.data,
        error: wrapped.error,
        message: wrapped.message
      };
    }
    
    // Backend returned raw data - wrap it
    return {
      success: true,
      data: responseData as T
    };
  } catch (error: unknown) {
    const axiosError = error as { response?: { data?: { error?: string; message?: string }; status?: number; statusText?: string } };
    const errorData = axiosError.response?.data || {};
    return {
      success: false,
      error: errorData.error || errorData.message || `HTTP ${axiosError.response?.status}: ${axiosError.response?.statusText}`
    };
  }
}

/**
 * Health API client
 */
export const healthApi = {
  /**
   * Get system health overview
   */
  async getOverview(capacityType?: string): Promise<ApiResponse<HealthOverview>> {
    const url = capacityType 
      ? `${API_BASE}/overview?capacityType=${encodeURIComponent(capacityType)}`
      : `${API_BASE}/overview`;
    
    return handleAxiosResponse(apiClient.get<HealthOverview>(url));
  },

  /**
   * Get component health details
   */
  async getComponentHealth(componentId: string): Promise<ApiResponse<ComponentHealth>> {
    return handleAxiosResponse(apiClient.get<ComponentHealth>(`${API_BASE}/components/${componentId}`));
  },

  /**
   * Get all component health statuses
   */
  async getAllComponents(): Promise<ApiResponse<ComponentHealth[]>> {
    return handleAxiosResponse(apiClient.get<ComponentHealth[]>(`${API_BASE}/components`));
  },

  /**
   * Get health trends over time
   */
  async getTrends(
    componentId?: string,
    hours: number = 24
  ): Promise<ApiResponse<HealthTrend[]>> {
    const params = new URLSearchParams({ hours: hours.toString() });
    if (componentId) {
      params.append('componentId', componentId);
    }

    return handleAxiosResponse(apiClient.get<HealthTrend[]>(`${API_BASE}/trends?${params}`));
  },

  /**
   * Get active alerts
   * T040: Routes to /critical-alerts (the only alerts route on /api/health)
   * Maps snake_case backend fields → camelCase frontend Alert interface
   */
  async getAlerts(
    severity?: string,
    capacityType?: string,
    limit: number = 50
  ): Promise<ApiResponse<Alert[]>> {
    const params = new URLSearchParams({ limit: limit.toString() });
    if (severity) {
      params.append('severity', severity);
    }
    if (capacityType) {
      params.append('capacityType', capacityType);
    }

    const raw = await handleAxiosResponse<Record<string, unknown>[]>(
      apiClient.get(`${API_BASE}/critical-alerts?${params}`)
    );
    if (!raw.success || !raw.data) return { success: raw.success, error: raw.error } as ApiResponse<Alert[]>;

    // Map snake_case DB fields → camelCase frontend interface
    const severityMap: Record<string, Alert['severity']> = {
      'Critical': 'critical', 'Warning': 'high', 'Info': 'info',
      'critical': 'critical', 'high': 'high', 'medium': 'medium', 'low': 'low', 'info': 'info',
    };
    const mapped: Alert[] = raw.data.map((a) => ({
      id: (a.alert_id || a.id || '') as string,
      severity: severityMap[(a.severity as string) || 'info'] || 'info',
      title: (a.title || '') as string,
      message: (a.description || a.message || '') as string,
      source: (a.resource_type || a.source || 'system') as string,
      capacityType: (a.capacity_type || a.capacityType) as string | undefined,
      timestamp: (a.created_at || a.timestamp || new Date().toISOString()) as string,
      acknowledged: Boolean(a.is_resolved || a.acknowledged),
      resolvedAt: (a.resolved_at || a.resolvedAt) as string | undefined,
    }));
    return { success: true, data: mapped };
  },

  /**
   * Get alert summary counts
   */
  async getAlertSummary(capacityType?: string): Promise<ApiResponse<AlertSummary>> {
    const url = capacityType 
      ? `${API_BASE}/alerts/summary?capacityType=${encodeURIComponent(capacityType)}`
      : `${API_BASE}/alerts/summary`;
    
    return handleAxiosResponse(apiClient.get<AlertSummary>(url));
  },

  /**
   * Acknowledge an alert
   */
  async acknowledgeAlert(alertId: string): Promise<ApiResponse<Alert>> {
    return handleAxiosResponse(apiClient.post<Alert>(`${API_BASE}/alerts/${alertId}/acknowledge`));
  },

  /**
   * Resolve an alert
   */
  async resolveAlert(alertId: string, resolution?: string): Promise<ApiResponse<Alert>> {
    return handleAxiosResponse(apiClient.post<Alert>(`${API_BASE}/alerts/${alertId}/resolve`, { resolution }));
  },

  /**
   * Get dependency graph
   */
  async getDependencies(capacityType?: string): Promise<ApiResponse<DependencyHealth[]>> {
    const url = capacityType 
      ? `${API_BASE}/dependencies?capacityType=${encodeURIComponent(capacityType)}`
      : `${API_BASE}/dependencies`;
    
    return handleAxiosResponse(apiClient.get<DependencyHealth[]>(url));
  },

  /**
   * Calculate blast radius for a component
   */
  async getBlastRadius(componentId: string): Promise<ApiResponse<{
    affectedComponents: string[];
    impactLevel: string;
    estimatedDowntime: string;
  }>> {
    return handleAxiosResponse(apiClient.get<{
      affectedComponents: string[];
      impactLevel: string;
      estimatedDowntime: string;
    }>(`${API_BASE}/components/${componentId}/blast-radius`));
  },

  /**
   * Run health check
   */
  async runHealthCheck(componentId?: string): Promise<ApiResponse<{
    checkId: string;
    status: string;
    startedAt: string;
  }>> {
    const body = componentId ? { componentId } : {};
    
    return handleAxiosResponse(apiClient.post<{
      checkId: string;
      status: string;
      startedAt: string;
    }>(`${API_BASE}/check`, body));
  },

  /**
   * Get SLA status
   */
  async getSlaStatus(capacityType?: string): Promise<ApiResponse<{
    tier: string;
    currentUptime: number;
    targetUptime: number;
    compliance: boolean;
    period: string;
  }>> {
    const url = capacityType 
      ? `${API_BASE}/sla?capacityType=${encodeURIComponent(capacityType)}`
      : `${API_BASE}/sla`;
    
    return handleAxiosResponse(apiClient.get<{
      tier: string;
      currentUptime: number;
      targetUptime: number;
      compliance: boolean;
      period: string;
    }>(url));
  }
};

export default healthApi;